/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";
import { mergeInnings } from "../matchPages/live/helpers";
import type { MatchInfo } from "../matchPages/live/helpers";
import { buildAllVms, buildMatchVm, type MatchVm } from "../tournamentPage/matchVm";
import { fetchAll, type MatchBucket } from "../tournamentPage/useTournamentMatches";
import { orderEmbedMatches } from "./order";

export const EMBED_POLL_MS = 20_000;

const statusOf = (s?: string | null) => (s ?? "").toUpperCase().replace(/\s+/g, "_");
export const bucketOf = (status?: string | null): MatchBucket => {
  const s = statusOf(status);
  if (s === "IN_PROGRESS") return "live";
  if (s === "SCHEDULED" || s === "UPCOMING" || s === "") return "up";
  return "past";
};

/** Re-renders every `ms` so "has the start time passed yet" is re-evaluated without a reload. */
const useNow = (ms: number) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
};

/**
 * One match, kept fresh. Polls while it is live, and while it is scheduled but its start time has passed
 * (so it flips to live on its own). Stops once it has ended or is still in the future.
 */
export const useEmbedMatch = (matchId: string | null | undefined, seed?: any) => {
  const now = useNow(15_000);
  const q = useQuery<MatchInfo>({
    queryKey: ["embedMatch", matchId],
    enabled: !!matchId,
    staleTime: EMBED_POLL_MS / 2,
    queryFn: async () => {
      const info: MatchInfo = (await Api.get(`${QUERY_KEY.getMatchInfo}/${matchId}`)).data;
      if (bucketOf(info?.matchStatus) === "up") return info;
      const scores = (await Api.get(`${QUERY_KEY.getScoreByMatch}/${matchId}`).catch(() => null))?.data?.inningScoreCardList;
      return { ...info, inningScorecardList: mergeInnings(info, scores) } as MatchInfo;
    },
    refetchInterval: (query) => {
      const d: any = query.state.data ?? seed;
      const b = bucketOf(d?.matchStatus);
      if (b === "live") return EMBED_POLL_MS;
      const start = d?.startDate ? new Date(d.startDate).getTime() : NaN;
      return b === "up" && !Number.isNaN(start) && Date.now() >= start ? EMBED_POLL_MS : false;
    },
  });

  const raw: any = q.data ?? seed;
  const vm = useMemo<MatchVm | null>(() => (raw ? buildMatchVm(raw, bucketOf(raw.matchStatus)) : null), [raw]);
  const start = raw?.startDate ? new Date(raw.startDate).getTime() : NaN;
  // A scheduled match with no start time yet never polls; one whose time passed keeps polling until it goes live.
  const awaitingStart = vm?.bucket === "up" && !Number.isNaN(start) && now >= start;
  return { vm, raw, now, isLoading: q.isLoading && !seed, isError: q.isError && !raw, polling: vm?.bucket === "live" || awaitingStart };
};

/** Every match of a tournament in arrow order (live, nearest upcoming, then finished), plus which one to open first. */
export const useEmbedTournament = (tournamentId?: string, preferredId?: string | null) => {
  const useList = (bucket: MatchBucket) =>
    useQuery({ queryKey: ["embedList", tournamentId, bucket], queryFn: () => fetchAll(tournamentId as string, bucket), enabled: !!tournamentId, staleTime: 60_000 });
  const live = useList("live"), up = useList("up"), past = useList("past");

  const vms = useMemo(() => {
    return orderEmbedMatches(buildAllVms(live.data ?? [], up.data ?? [], past.data ?? []));
  }, [live.data, up.data, past.data]);

  const initialId = useMemo(() => {
    if (preferredId && vms.some((m) => m.id === preferredId)) return preferredId;
    return vms[0]?.id ?? null;
  }, [vms, preferredId]);

  return { vms, initialId, loading: live.isLoading || up.isLoading || past.isLoading, error: live.isError && up.isError && past.isError };
};
